import { FC } from 'react';
import {
  FontAwesomeIcon,
  FontAwesomeIconProps,
} from '@fortawesome/react-fontawesome';
import { BoxV2 as Box, Flex } from 'portal-commons';

import Divider from './Divider';

interface RcsSectionBlockProps {
  icon?: FontAwesomeIconProps['icon'];
  title: string;
  showDivider?: boolean;
  'data-testid'?: string;
  sx?: object;
}

type RcsSectionBlockComponent = FC<RcsSectionBlockProps> & {
  SubSection: typeof SubSection;
};

const RcsSectionBlock: RcsSectionBlockComponent = ({
  icon,
  title,
  showDivider = true,
  children,
  'data-testid': dataTestId,
  sx,
}) => {
  return (
    <Flex
      as="section"
      sx={{
        flexDirection: 'column',
        paddingY: 'xl',
        paddingX: 'xxl',
        border: '1px solid',
        borderColor: 't.black20',
        backgroundColor: 't.white',
        ...sx,
      }}
      data-testid={dataTestId ?? 'rcsSection'}
    >
      <Flex
        sx={{
          alignItems: 'center',
          flexDirection: 'row',
          color: 't.black70',
          marginBottom: showDivider ? 's' : 'l',
        }}
      >
        {icon && (
          <Box sx={{ marginRight: 'xs' }}>
            <FontAwesomeIcon icon={icon} fontSize="24px" />
          </Box>
        )}
        <Box as="h3" sx={{ fontSize: 'H500', fontWeight: 'semiBold' }}>
          {title}
        </Box>
      </Flex>
      {showDivider && <Divider />}
      <Flex sx={{ flexDirection: 'column', gap: 'l' }}>
        <div data-testid="section-content">{children}</div>
      </Flex>
    </Flex>
  );
};

interface SubSectionProps {
  title: string;
  children: React.ReactNode;
}

export const SubSection: FC<SubSectionProps> = ({ title, children }) => (
  <Flex sx={{ flexDirection: 'column', gap: 'l' }}>
    <div>
      <Box
        as="h4"
        sx={{
          fontSize: 'H400',
          fontWeight: 'semiBold',
          color: 't.black70',
          mb: 'xs',
        }}
      >
        {title}
      </Box>
      <Divider />
      <div>{children}</div>
    </div>
  </Flex>
);

RcsSectionBlock.SubSection = SubSection;

export default RcsSectionBlock;
